/** Stato form movimento (wizard + editor bozze). */
import { getUserPreferences } from "@/components/PreferenzeOperative";
import { endpoints } from "@/api/client";

export interface MovimentoFormState {
  data: string;
  tipo: "Carico" | "Scarico";
  causale_operazione: string;
  anno: string;
  progressivo: string;
  eer: string;
  descrizione_eer: string;
  provenienza: string;
  stato_fisico: string;
  quantita: string;
  unita_misura: string;
  caratteristiche_pericolo: string[];
  destinato_attivita: string;
  annotazioni: string;
  numero_fir: string;
  data_inizio_trasporto: string;
  trasporto_transfrontaliero: boolean;
  peso_verificato_destino: string;
  data_fine_trasporto: string;
  produttore_cf: string;
  trasportatore_cf: string;
  destinatario_cf: string;
}

const LEGACY_CAUSALE_TO_OP: Record<string, string> = {
  C: "DT",
  S: "aT",
  CC: "I",
  RP: "RE",
  GP: "GP",
  CS: "CS",
};

function asRecord(v: unknown): Record<string, unknown> {
  return v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
}

function cfFromSoggetto(s: unknown): string {
  const r = asRecord(s);
  return String(r.codice_fiscale ?? r.cf ?? "");
}

function hpFromPayload(p: Record<string, unknown>, rifiuto: Record<string, unknown>): string[] {
  const raw = p.caratteristiche_pericolo ?? rifiuto.caratteristiche_pericolo ?? rifiuto.hp;
  if (Array.isArray(raw)) return raw.map(String).filter(Boolean);
  if (typeof raw === "string" && raw.trim()) {
    return raw.split(/[,;]/).map((s) => s.trim()).filter(Boolean);
  }
  return [];
}

function resolveCausale(p: Record<string, unknown>, rif: Record<string, unknown>): string {
  const op = rif.causale_operazione ?? p.causale_operazione;
  if (op) return String(op);
  const legacy = p.causale;
  if (legacy) return LEGACY_CAUSALE_TO_OP[String(legacy)] ?? String(legacy);
  return "DT";
}

function resolveTipo(
  p: Record<string, unknown>,
  rif: Record<string, unknown>,
  causale: string,
): "Carico" | "Scarico" {
  const t = p.tipo;
  if (t === "Carico" || t === "Scarico") return t;
  const cs = String(rif.causale_operazione_cs ?? "").toLowerCase();
  if (cs.includes("scarico")) return "Scarico";
  if (cs.includes("carico")) return "Carico";
  if (causale === "aT" || causale === "RE") return "Scarico";
  return "Carico";
}

/** RENTRI accetta solo R1–R13, D1–D15 o CR: valori diversi vanno riselezionati. */
function validDestinatoAttivita(raw: unknown): string {
  const v = String(raw ?? "").trim();
  return /^(R([1-9]|1[0-3])|D([1-9]|1[0-5])|CR)$/.test(v) ? v : "";
}

export function defaultMovimentoForm(): MovimentoFormState {
  return {
    data: new Date().toISOString().slice(0, 10),
    tipo: getUserPreferences().movimento_tipo_default ?? "Carico",
    causale_operazione: "DT",
    anno: "",
    progressivo: "",
    eer: "",
    descrizione_eer: "",
    provenienza: "S",
    stato_fisico: "S",
    quantita: "100",
    unita_misura: "KG",
    caratteristiche_pericolo: [],
    destinato_attivita: "R13",
    annotazioni: "",
    numero_fir: "",
    data_inizio_trasporto: "",
    trasporto_transfrontaliero: false,
    peso_verificato_destino: "",
    data_fine_trasporto: "",
    produttore_cf: "",
    trasportatore_cf: "",
    destinatario_cf: "",
  };
}

export function payloadToMovimentoForm(payload: unknown): MovimentoFormState {
  const base = defaultMovimentoForm();
  const p = asRecord(payload);
  if (!Object.keys(p).length) return base;

  const rif = asRecord(p.riferimenti);
  const rifiuto = asRecord(p.rifiuto);
  const isRentri = Boolean(p.riferimenti || p.rifiuto);

  if (isRentri) {
    const fir = asRecord(p.integrazione_fir);
    const esito = asRecord(p.esito);
    const qty = asRecord(rifiuto.quantita);
    const num = asRecord(rif.numero_registrazione);
    const causale = resolveCausale(p, rif);
    let data = "";
    const dor = rif.data_ora_registrazione ?? p.data;
    if (dor) data = String(dor).slice(0, 10);

    return {
      ...base,
      data: data || base.data,
      tipo: resolveTipo(p, rif, causale),
      causale_operazione: causale,
      anno: num.anno != null ? String(num.anno) : "",
      progressivo: num.progressivo != null ? String(num.progressivo) : "",
      eer: String(rifiuto.codice_eer ?? p.eer ?? "").replace(/\./g, ""),
      descrizione_eer: String(
        rifiuto.descrizione_eer ?? rifiuto.descrizione ?? p.descrizione_eer ?? "",
      ),
      provenienza: String(rifiuto.provenienza ?? p.provenienza ?? "S"),
      stato_fisico: String(rifiuto.stato_fisico ?? p.stato_fisico ?? "S"),
      quantita:
        qty.valore != null
          ? String(qty.valore)
          : p.quantita != null
            ? String(p.quantita)
            : base.quantita,
      unita_misura: String(
        qty.unita_misura ?? qty.unitaMisura ?? p.unita_misura ?? "KG",
      ).toUpperCase(),
      caratteristiche_pericolo: hpFromPayload(p, rifiuto),
      destinato_attivita: validDestinatoAttivita(
        rifiuto.destinato_attivita ?? p.destinato_attivita ?? base.destinato_attivita,
      ),
      annotazioni: String(p.annotazioni ?? rif.annotazioni ?? ""),
      numero_fir: String(fir.numero_fir ?? rif.numero_fir ?? p.numero_fir ?? ""),
      data_inizio_trasporto: String(
        fir.data_inizio_trasporto ?? rif.data_inizio_trasporto ?? p.data_inizio_trasporto ?? "",
      ).slice(0, 10),
      trasporto_transfrontaliero: Boolean(
        fir.trasporto_transfrontaliero ??
          rif.trasporto_transfrontaliero ??
          p.trasporto_transfrontaliero,
      ),
      peso_verificato_destino: String(
        esito.peso_verificato_destino ??
          rif.peso_verificato_destino ??
          p.peso_verificato_destino ??
          "",
      ),
      data_fine_trasporto: String(
        esito.data_fine_trasporto ?? rif.data_fine_trasporto ?? p.data_fine_trasporto ?? "",
      ).slice(0, 10),
      produttore_cf: cfFromSoggetto(p.produttore),
      trasportatore_cf: cfFromSoggetto(p.trasportatore),
      destinatario_cf: cfFromSoggetto(p.destinatario),
    };
  }

  const causale = resolveCausale(p, rif);
  let data = String(p.data ?? base.data);
  if (data.includes("T")) data = data.slice(0, 10);

  return {
    ...base,
    data,
    tipo: resolveTipo(p, rif, causale),
    causale_operazione: causale,
    anno: p.anno != null ? String(p.anno) : "",
    progressivo: p.progressivo != null ? String(p.progressivo) : "",
    eer: String(p.eer ?? "").replace(/\./g, ""),
    descrizione_eer: String(p.descrizione_eer ?? ""),
    provenienza: String(p.provenienza ?? "S"),
    stato_fisico: String(p.stato_fisico ?? "S"),
    quantita: p.quantita != null ? String(p.quantita) : base.quantita,
    unita_misura: String(p.unita_misura ?? "KG").toUpperCase(),
    caratteristiche_pericolo: hpFromPayload(p, rifiuto),
    destinato_attivita: validDestinatoAttivita(p.destinato_attivita ?? base.destinato_attivita),
    annotazioni: String(p.annotazioni ?? ""),
    numero_fir: String(p.numero_fir ?? ""),
    data_inizio_trasporto: String(p.data_inizio_trasporto ?? "").slice(0, 10),
    trasporto_transfrontaliero: Boolean(p.trasporto_transfrontaliero),
    peso_verificato_destino:
      p.peso_verificato_destino != null ? String(p.peso_verificato_destino) : "",
    data_fine_trasporto: String(p.data_fine_trasporto ?? "").slice(0, 10),
    produttore_cf: cfFromSoggetto(p.produttore),
    trasportatore_cf: cfFromSoggetto(p.trasportatore),
    destinatario_cf: cfFromSoggetto(p.destinatario),
  };
}

export function movimentoFormToDto(f: MovimentoFormState): Record<string, unknown> {
  const hp = f.caratteristiche_pericolo.filter(Boolean);
  return {
    data: f.data,
    tipo: f.tipo,
    causale_operazione: f.causale_operazione,
    anno: f.anno ? Number(f.anno) : undefined,
    progressivo: f.progressivo ? Number(f.progressivo) : undefined,
    eer: f.eer.replace(/\./g, ""),
    descrizione_eer: f.descrizione_eer || undefined,
    provenienza: f.provenienza || undefined,
    stato_fisico: f.stato_fisico || undefined,
    quantita: f.quantita ? Number(f.quantita) : undefined,
    unita_misura: f.unita_misura || undefined,
    caratteristiche_pericolo: hp.length ? hp : undefined,
    destinato_attivita: f.destinato_attivita || undefined,
    annotazioni: f.annotazioni || undefined,
    numero_fir: f.numero_fir || undefined,
    data_inizio_trasporto: f.data_inizio_trasporto || undefined,
    trasporto_transfrontaliero: f.trasporto_transfrontaliero,
    peso_verificato_destino: f.peso_verificato_destino
      ? Number(f.peso_verificato_destino)
      : undefined,
    data_fine_trasporto: f.data_fine_trasporto || undefined,
    produttore: f.produttore_cf ? { codice_fiscale: f.produttore_cf } : undefined,
    trasportatore: f.trasportatore_cf ? { codice_fiscale: f.trasportatore_cf } : undefined,
    destinatario: f.destinatario_cf ? { codice_fiscale: f.destinatario_cf } : undefined,
  };
}

/**
 * Il form gestisce solo il codice fiscale dei soggetti: se non è cambiato,
 * conserva i dettagli già presenti nella bozza (denominazione, indirizzo,
 * autorizzazione, albo) richiesti da RENTRI per gli scarichi.
 */
function keepSoggettiDettaglio(
  payload: Record<string, unknown>,
  previous: unknown,
): Record<string, unknown> {
  const prev = asRecord(previous);
  for (const key of ["produttore", "trasportatore", "destinatario"]) {
    const cur = asRecord(payload[key]);
    const old = asRecord(prev[key]);
    if (cur.codice_fiscale && cur.codice_fiscale === old.codice_fiscale) {
      payload[key] = { ...old, ...cur };
    }
  }
  return payload;
}

export async function buildMovimentoPayload(
  form: MovimentoFormState,
  previous?: unknown,
): Promise<Record<string, unknown>> {
  const dto = movimentoFormToDto(form);
  const norm = await endpoints.movimenti.normalize(dto);
  if (Array.isArray(norm.items) && norm.items.length === 1) {
    return keepSoggettiDettaglio(norm.items[0] as Record<string, unknown>, previous);
  }
  return keepSoggettiDettaglio(dto, previous);
}
